import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  Build as ToolIcon,
  Speed as SpeedIcon,
  Person as PersonIcon,
  Money as MoneyIcon,
} from '@mui/icons-material';
import { apiService, AnalyticsData } from '../services/apiService';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'customer' | 'agent' | 'admin';
}

interface AnalyticsProps {
  user: User;
  onNotification: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const Analytics: React.FC<AnalyticsProps> = ({ user, onNotification }) => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [period, setPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAnalytics(period);
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      onNotification('Failed to load analytics data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getPeriodLabel = (period: string) => {
    switch (period) {
      case '24h': return 'Last 24 Hours';
      case '7d': return 'Last 7 Days';
      case '30d': return 'Last 30 Days';
      case '90d': return 'Last 90 Days';
      default: return 'Last 7 Days';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Typography>Loading analytics...</Typography>
      </Box>
    );
  }

  if (!analytics) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="textSecondary">
          No analytics data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Analytics
          </Typography>
          <Typography variant="body2" color="textSecondary">
            AI Agent performance and business intelligence
          </Typography>
        </Box>
        
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Time Period</InputLabel>
          <Select
            value={period}
            label="Time Period"
            onChange={(e) => setPeriod(e.target.value)}
          >
            <MenuItem value="24h">Last 24 Hours</MenuItem>
            <MenuItem value="7d">Last 7 Days</MenuItem>
            <MenuItem value="30d">Last 30 Days</MenuItem>
            <MenuItem value="90d">Last 90 Days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Key Metrics */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ backgroundColor: '#1fb8cc', mr: 2 }}>
                  <ChatIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Conversations
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 600, color: '#1fb8cc' }}>
                {analytics.totalConversations}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {getPeriodLabel(period)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ backgroundColor: '#95c100', mr: 2 }}>
                  <TrendingUpIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Resolved
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 600, color: '#95c100' }}>
                {analytics.resolvedQueries}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {((analytics.resolvedQueries / analytics.totalConversations) * 100).toFixed(1)}% success rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ backgroundColor: '#95c100', mr: 2 }}>
                  <SpeedIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Avg Response
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 600, color: '#95c100' }}>
                {analytics.averageResponseTime}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                milliseconds
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ backgroundColor: '#95c100', mr: 2 }}>
                  <PeopleIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Satisfaction
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 600, color: '#95c100' }}>
                {analytics.customerSatisfaction.toFixed(1)}/5.0
              </Typography>
              <Typography variant="caption" color="textSecondary">
                customer satisfaction
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Issues */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Top Issues
              </Typography>
              
              {analytics.topIssues.length > 0 ? (
                <List>
                  {analytics.topIssues.map((issue, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText
                        primary={issue.issue}
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={(issue.count / analytics.topIssues[0].count) * 100}
                              sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption" sx={{ minWidth: 40 }}>
                              {issue.count}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No issues data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* MCP Tool Usage */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                MCP Tool Usage
              </Typography>
              
              {analytics.mcpToolUsage.length > 0 ? (
                <List>
                  {analytics.mcpToolUsage.map((tool, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ToolIcon sx={{ fontSize: 16, color: '#1fb8cc' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {tool.tool}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={(tool.usage / analytics.mcpToolUsage[0].usage) * 100}
                              sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption" sx={{ minWidth: 40 }}>
                              {tool.usage}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No tool usage data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Conversation Trends */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Conversation Trends
              </Typography>
              
              {analytics.conversationTrends.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Conversations</TableCell>
                        <TableCell align="right">Trend</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.conversationTrends.map((trend, index) => {
                        const prevCount = index > 0 ? analytics.conversationTrends[index - 1].count : trend.count;
                        const change = trend.count - prevCount;
                        const changePercent = prevCount > 0 ? ((change / prevCount) * 100).toFixed(1) : '0';
                        
                        return (
                          <TableRow key={trend.date}>
                            <TableCell>
                              {new Date(trend.date).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right">
                              {trend.count}
                            </TableCell>
                            <TableCell align="right">
                              {index > 0 && (
                                <Chip
                                  label={`${change >= 0 ? '+' : ''}${changePercent}%`}
                                  size="small"
                                  color={change >= 0 ? 'success' : 'error'}
                                  variant="outlined"
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No trend data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics; 